import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, Shield, Snowflake, Lock, AlertTriangle, RotateCcw,
  Loader2, RefreshCw, History, Database, ShieldCheck, ShieldAlert,
  Clock, ChevronRight,
} from 'lucide-react';

const COLORS = {
  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  amber:   'bg-amber-500/10 text-amber-300 border-amber-500/30',
  rose:    'bg-rose-500/10 text-rose-300 border-rose-500/30',
  sky:     'bg-sky-500/10 text-sky-300 border-sky-500/30',
  violet:  'bg-violet-500/10 text-violet-300 border-violet-500/30',
  slate:   'bg-slate-500/10 text-slate-300 border-slate-500/30',
};

const formatNumber = (n) => (n ?? 0).toLocaleString('en-US');
const formatPercent = (n) => (n ?? 0).toFixed(1) + '%';
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US',
    { dateStyle:'short', timeStyle:'short' }); }
  catch { return iso; }
};

function Hero({ label, label_ar, value, sub, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid}
      className={`rounded-xl border-2 ${COLORS[accent]} p-5 backdrop-blur-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest opacity-60">{label}</div>
          <div className="text-[10px] opacity-50 mt-0.5">{label_ar}</div>
        </div>
        {Icon && <Icon size={22} className="opacity-70" />}
      </div>
      <div className="mt-3 text-4xl font-mono font-bold">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-2">{sub}</div>}
    </div>
  );
}

function Pillar({ id, title, value, status, detail }) {
  const color = status === 'clean' ? 'emerald'
              : status === 'warn'  ? 'amber'
              : status === 'fail'  ? 'rose'
              : 'slate';
  const Icon = status === 'clean' ? ShieldCheck
             : status === 'fail'  ? ShieldAlert : Shield;
  return (
    <div data-testid={`pillar-${id}`}
      className={`rounded-lg border ${COLORS[color]} p-4`}>
      <div className="flex items-center gap-2">
        <Icon size={16} className="opacity-80" />
        <div className="text-xs uppercase tracking-wide font-semibold">{title}</div>
      </div>
      <div className="mt-2 text-2xl font-mono">{value}</div>
      {detail && <div className="text-[11px] opacity-60 mt-1">{detail}</div>}
    </div>
  );
}

function CollectionHeatmap({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div data-testid="heatmap-empty"
        className="text-sm opacity-60 italic">
        No active leaks. System is clean.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider opacity-60">
          <tr className="border-b border-slate-700/50">
            <th className="text-left py-2 pr-4">Collection</th>
            <th className="text-right py-2 px-3">Total Missing</th>
            <th className="text-right py-2 px-3">Safe</th>
            <th className="text-right py-2 px-3">Needs Evidence</th>
            <th className="text-right py-2 px-3">Drop</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const safe = r.counts?.SAFE_RECOVERABLE || 0;
            const ne   = r.counts?.NEEDS_EVIDENCE || 0;
            const drop = r.counts?.DROP_CANDIDATE || 0;
            return (
              <tr key={r.collection}
                data-testid={`heatmap-row-${r.collection}`}
                className="border-b border-slate-800/40 hover:bg-slate-800/30">
                <td className="py-2 pr-4 font-mono text-xs">{r.collection}</td>
                <td className="py-2 px-3 text-right font-mono">{r.total_missing}</td>
                <td className={`py-2 px-3 text-right font-mono ${safe > 0 ? 'text-emerald-300' : 'opacity-30'}`}>{safe}</td>
                <td className={`py-2 px-3 text-right font-mono ${ne > 0 ? 'text-amber-300' : 'opacity-30'}`}>{ne}</td>
                <td className={`py-2 px-3 text-right font-mono ${drop > 0 ? 'text-rose-300' : 'opacity-30'}`}>{drop}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TimelineRow({ kind, run, onRollback }) {
  const kindBadge = {
    recovery:    { color: 'sky',     label: 'Recovery' },
    forensic:    { color: 'violet',  label: 'Forensic' },
    quarantine:  { color: 'amber',   label: 'Quarantine' },
  }[kind] || { color: 'slate', label: kind };

  const count = run.records_changed ?? run.total_marked ?? run.records_marked ?? 0;
  const reverted = run.rolled_back || run.reverted;
  return (
    <div data-testid={`timeline-row-${run.id}`}
      className="flex items-center gap-3 py-2 border-b border-slate-800/40">
      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold border ${COLORS[kindBadge.color]}`}>
        {kindBadge.label}
      </span>
      <span className="font-mono text-xs opacity-70 w-20">{run.id?.slice(0, 8)}</span>
      <span className="text-xs opacity-60 flex-1">
        <Clock size={11} className="inline mr-1 opacity-60" />
        {formatDate(run.started_at || run.completed_at)}
      </span>
      <span className="font-mono text-sm">{formatNumber(count)} records</span>
      {reverted && (
        <span className="text-[10px] uppercase border border-slate-600 text-slate-400 px-1.5 rounded">
          Reverted
        </span>
      )}
      {!reverted && onRollback && (
        <button onClick={() => onRollback(kind, run.id)}
          data-testid={`rollback-${run.id}`}
          className="text-[10px] uppercase opacity-60 hover:opacity-100 hover:text-rose-300 px-2 py-1 rounded transition">
          <RotateCcw size={11} className="inline mr-1" />
          Rollback
        </button>
      )}
    </div>
  );
}

export default function SystemHealthDashboard() {
  const [snapshot, setSnapshot] = useState(null);
  const [classification, setClassification] = useState(null);
  const [recoveryRuns, setRecoveryRuns] = useState([]);
  const [forensicRuns, setForensicRuns] = useState([]);
  const [quarantineRuns, setQuarantineRuns] = useState([]);
  const [gateLog, setGateLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    const endpoints = [
      ['snapshot',       '/legacy-audit/integrity-snapshot'],
      ['classification', '/legacy-audit/classification-scan?sample_limit=1'],
      ['recoveryRuns',   '/legacy-audit/recovery-runs?limit=20'],
      ['forensicRuns',   '/legacy-audit/forensics/freeze-runs?limit=20'],
      ['quarantineRuns', '/legacy-audit/quarantine-runs?limit=20'],
      ['gateLog',        '/legacy-audit/validation-gate/log?limit=1'],
    ];
    const results = await Promise.allSettled(
      endpoints.map(([, url]) => axiosInstance.get(url))
    );
    const errors = [];
    results.forEach((r, i) => {
      const [key] = endpoints[i];
      if (r.status === 'fulfilled') {
        const data = r.value.data;
        if (key === 'snapshot')       setSnapshot(data);
        if (key === 'classification') setClassification(data);
        if (key === 'recoveryRuns')   setRecoveryRuns(data.runs || []);
        if (key === 'forensicRuns')   setForensicRuns(data.runs || []);
        if (key === 'quarantineRuns') setQuarantineRuns(data.runs || []);
        if (key === 'gateLog')        setGateLog(data);
      } else {
        errors.push(`${key}: ${r.reason?.response?.status || r.reason?.message}`);
      }
    });
    if (errors.length) toast.error('Some endpoints failed: ' + errors.join(' · '));
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    // Defer first fetch off the render cycle to satisfy
    // react-hooks/set-state-in-effect.
    const handle = setTimeout(fetchAll, 0);
    const interval = setInterval(fetchAll, 30000);
    return () => {
      clearTimeout(handle);
      clearInterval(interval);
    };
  }, [fetchAll]);

  const handleRollback = async (kind, runId) => {
    if (!window.confirm(`Rollback ${kind} run ${runId.slice(0, 8)}? This is reversible.`)) return;
    try {
      let url;
      if (kind === 'recovery')   url = `/legacy-audit/recovery-runs/${runId}/rollback`;
      else if (kind === 'forensic') url = `/legacy-audit/forensics/freeze-runs/${runId}/unfreeze`;
      else if (kind === 'quarantine') url = `/legacy-audit/quarantine-runs/${runId}/unquarantine`;
      const r = await axiosInstance.post(url);
      toast.success(`Rolled back: ${r.data.reverted || r.data.records_changed || 'done'}`);
      fetchAll();
    } catch (e) {
      toast.error('Rollback failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return (
      <div data-testid="health-loading" className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin opacity-50" />
      </div>
    );
  }

  const headline = snapshot?.headline || {};
  const A = snapshot?.A_data_integrity || {};
  const B = snapshot?.B_state_inconsistency || {};
  const C = snapshot?.C_bypass_paths || {};
  const D = snapshot?.D_visibility_mismatch || {};
  const E = snapshot?.E_forensic_layer || {};
  const F = snapshot?.F_quarantine_layer || {};
  const gateCounts = gateLog?.totals_by_verdict || {};

  // Mix and sort all runs by date desc
  const timeline = [
    ...recoveryRuns.map(r => ({ ...r, _kind: 'recovery' })),
    ...forensicRuns.map(r => ({ ...r, _kind: 'forensic' })),
    ...quarantineRuns.map(r => ({ ...r, _kind: 'quarantine' })),
  ].sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''))
   .slice(0, 30);

  return (
    <div data-testid="system-health-dashboard"
      className="space-y-6 text-slate-200 p-6 bg-slate-950 min-h-screen">

      {/* ── Header ────────────────────────────────────────── */}
      <div className="flex items-start justify-between border-b border-slate-800 pb-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity size={28} className="text-emerald-400" />
            System Health Dashboard
          </h1>
          <p className="text-xs opacity-60 mt-1 font-mono">
            لوحة صحة النظام — Three-Layer Data Architecture (Business / Forensic / Quarantine)
          </p>
          <p className="text-[10px] opacity-40 mt-1">
            Snapshot at {formatDate(snapshot?.captured_at)} • auto-refresh every 30s
          </p>
        </div>
        <button onClick={fetchAll} disabled={refreshing}
          data-testid="refresh-button"
          className="px-3 py-2 rounded border border-slate-700 hover:bg-slate-800 transition text-xs flex items-center gap-2">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* ── Hero: three layers ────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Hero
          testid="hero-active"
          label="Active Orphans"
          label_ar="السجلات المعلّقة"
          value={formatNumber(headline.active_orphans)}
          sub={`${formatNumber(A.missing_company_id)} missing company_id · ${formatNumber(A.missing_record_id)} missing id · ${formatNumber(A.orphans)} broken FK`}
          icon={AlertTriangle}
          accent={headline.active_orphans === 0 ? 'emerald' : headline.active_orphans < 100 ? 'amber' : 'rose'}
        />
        <Hero
          testid="hero-forensic"
          label="Forensic Isolated"
          label_ar="الطبقة الجنائية"
          value={formatNumber(headline.forensic_isolated)}
          sub={`${formatNumber(E.detached_cascade_workflows)} workflows · ${formatNumber(E.detached_cascade_workflow_steps)} steps`}
          icon={Snowflake}
          accent="violet"
        />
        <Hero
          testid="hero-quarantine"
          label="Quarantined"
          label_ar="عزل تشغيلي"
          value={formatNumber(headline.quarantined)}
          sub={`Excluded from BUSINESS view layer · ${Object.keys(F.by_collection || {}).length} collections`}
          icon={Lock}
          accent="amber"
        />
      </div>

      {/* ── Integrity Pillars (A-F) ───────────────────────── */}
      <div>
        <h2 className="text-sm uppercase tracking-widest opacity-70 mb-3 flex items-center gap-2">
          <Shield size={14} /> Integrity Pillars
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Pillar id="A" title="Data Integrity"
            value={formatNumber(A.subtotal)}
            status={A.subtotal === 0 ? 'clean' : A.subtotal < 100 ? 'warn' : 'fail'}
            detail="orphans + missing fields" />
          <Pillar id="B" title="State Validity"
            value={formatNumber(B.records_with_invalid_state)}
            status={B.records_with_invalid_state === 0 ? 'clean' : 'fail'}
            detail="records w/ invalid approval_status" />
          <Pillar id="C" title="Bypass Paths"
            value={formatNumber(C.router_callsites_writing_status_directly)}
            status={C.router_callsites_writing_status_directly === 0 ? 'clean' : 'fail'}
            detail={`${formatNumber(C.enforcement_log_events_total)} enforcement events`} />
          <Pillar id="D" title="Visibility"
            value={formatNumber(D.router_callsites_still_inline_approved_filter)}
            status={D.router_callsites_still_inline_approved_filter === 0 ? 'clean' : 'fail'}
            detail="inline filter call-sites" />
          <Pillar id="E" title="Forensic Layer"
            value={formatNumber(E.total_isolated)}
            status="warn"
            detail="isolated, not active" />
          <Pillar id="F" title="Quarantine Layer"
            value={formatNumber(F.total_quarantined)}
            status="warn"
            detail="excluded from business" />
        </div>
      </div>

      {/* ── Gate Activity ─────────────────────────────────── */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-sm uppercase tracking-widest opacity-70 mb-3 flex items-center gap-2">
          <ShieldCheck size={14} /> Validation Gate Activity
        </h2>
        <div className="grid grid-cols-4 gap-3">
          <div data-testid="gate-approved" className={`rounded p-3 border ${COLORS.emerald}`}>
            <div className="text-[10px] uppercase opacity-70">Approved</div>
            <div className="text-2xl font-mono mt-1">{formatNumber(gateCounts.APPROVED)}</div>
          </div>
          <div data-testid="gate-blocked" className={`rounded p-3 border ${COLORS.rose}`}>
            <div className="text-[10px] uppercase opacity-70">Blocked</div>
            <div className="text-2xl font-mono mt-1">{formatNumber(gateCounts.BLOCKED)}</div>
          </div>
          <div data-testid="gate-parent-unfit" className={`rounded p-3 border ${COLORS.rose}`}>
            <div className="text-[10px] uppercase opacity-70">Parent Unfit</div>
            <div className="text-2xl font-mono mt-1">{formatNumber(gateCounts.BLOCKED_PARENT_UNFIT)}</div>
          </div>
          <div data-testid="gate-insufficient" className={`rounded p-3 border ${COLORS.amber}`}>
            <div className="text-[10px] uppercase opacity-70">Insufficient</div>
            <div className="text-2xl font-mono mt-1">{formatNumber(gateCounts.INSUFFICIENT_EVIDENCE)}</div>
          </div>
        </div>
      </div>

      {/* ── Two-column: heatmap + timeline ───────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <h2 className="text-sm uppercase tracking-widest opacity-70 mb-4 flex items-center gap-2">
            <Database size={14} />
            Active Leak Heatmap
            <span className="text-[10px] opacity-50 normal-case tracking-normal ml-2">
              · {formatNumber(classification?.totals?.grand_total_missing)} records · {formatPercent(classification?.totals?.distribution_pct?.SAFE_RECOVERABLE)} safe
            </span>
          </h2>
          <CollectionHeatmap rows={classification?.by_collection} />
        </div>

        <div className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <h2 className="text-sm uppercase tracking-widest opacity-70 mb-4 flex items-center gap-2">
            <History size={14} /> Recent Runs Timeline
          </h2>
          {timeline.length === 0 ? (
            <div className="text-sm opacity-60 italic">No runs yet.</div>
          ) : (
            <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
              {timeline.map((r) => (
                <TimelineRow key={r.id}
                  kind={r._kind} run={r}
                  onRollback={handleRollback} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer link ───────────────────────────────────── */}
      <div className="text-[11px] opacity-50 flex items-center gap-2 pt-2 border-t border-slate-800">
        Need deeper recovery controls?
        <a href="/legacy-audit"
          data-testid="link-legacy-recovery"
          className="text-emerald-400 hover:underline inline-flex items-center gap-1">
          Open Legacy Recovery Center <ChevronRight size={11} />
        </a>
      </div>
    </div>
  );
}
